/* eslint-disable no-unused-expressions */

import React, { useEffect, useRef, useState } from "react";
import { PropTypes } from "prop-types";
import styled, { css } from "styled-components";
import { FiraSansBold, FontWeight, Text } from '../Text';
import Icon from "../Icon";
import Colors from "constants/Colors";
import Label from "./Label";
import ValidationError from "./ValidationError";
import Spinner, { SpinnerSize } from "../Spinner";
import Button from "../Button";
import _ from "lodash";
import { Breakpoints } from "../../constants/Breakpoints";
import { mediaHelper } from "../../utils/src";

export const InputSize = {
  SMALL: 1,
  NORMAL: 2,
};

const propTypes = {
  response: PropTypes.bool,
  type: PropTypes.string,
  placeholder: PropTypes.string,
  leftIcon: PropTypes.string,
  rightIcon: PropTypes.string,
  leftRender: PropTypes.func,
  rightRender: PropTypes.func,
  isLoading: PropTypes.bool,
  label: PropTypes.string,
  format: PropTypes.func,
  parse: PropTypes.func,
  field: PropTypes.any.isRequired,
  form: PropTypes.any.isRequired,
  isRequired: PropTypes.bool,
  tabIndex: PropTypes.number,
  onChange: PropTypes.func,
  onBlur: PropTypes.func,
  onFocus: PropTypes.func,
  onPressEnter: PropTypes.func,
  canGenerate: PropTypes.bool,
  fieldProps: PropTypes.any,
  smallInput: PropTypes.bool,
  readOnly: PropTypes.bool,
  inline: PropTypes.bool,
  inverseBlue: PropTypes.bool,
  size: PropTypes.oneOf(Object.values(InputSize)),
  valueCalendar: PropTypes.any,
};

const defaultProps = {
  canGenerate: true,
  response: false,
  type: "text",
  placeholder: "",
  leftIcon: null,
  rightIcon: null,
  validation: true,
  rightRender: null,
  label: null,
  format: (value) => value,
  parse: (value) => value,
  isLoading: false,
  isRequired: false,
  tabIndex: 0,
  onChange: (value) => value,
  onBlur: (value) => value,
  onFocus: (value) => value,
  onPressEnter: (value) => value,
  fieldProps: {},
  smallInput: false,
  readOnly: false,
  inline: false,
  inverseBlue: false,
  size: InputSize.NORMAL,
  valueCalendar: null,
};

// Mixins
const errorInputMixin = css`
  box-shadow: 0 0 0 3px ${Colors.DANGER};
  &:focus {
    box-shadow: 0 0 0 3px ${Colors.DANGER};
  }
`;

const FontIcon = styled(({ isOpen, ...props }) => <Icon {...props} />)`
  width: 20px;
  height: 20px;
  display: flex;
  justify-content: center;
  align-items: center;
  color: ${Colors.GRAY[0]};
  transition: transform 0.3s ease;
  ${({ isOpen }) =>
    isOpen &&
    css`
      color: ${Colors.SECONDARY};
      transform: rotate(180deg);
    `}
    
  margin: 1px;
  
  @media screen and (min-width: ${Breakpoints.sm}) {
    margin: 10px 6px 10px 10px;
  }
`;

// Components
export const InputContainer = styled.div`
  box-sizing: border-box;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: white;

  box-shadow: 0 0 0 1px ${Colors.GRAY[3]};

  
  transition: box-shadow 0.15s ease;
  margin-bottom: 10px;
  position: relative;
  height: 42px;

  ${({ warning, warningByHiddenQuestions }) =>
    (warning || warningByHiddenQuestions) &&
    css`
    box-shadow: 0 0 0 1px #FDC830;
  `}

  ${({ refused, refusedByHiddenQuestions }) =>
    (refused || refusedByHiddenQuestions) &&
    css`
      box-shadow: 0 0 0 1px #DB0F16;
  `}

  ${({ isOpen }) =>
    isOpen &&
    css`
      box-shadow: 0 0 0 3px ${Colors.SECONDARY};
      
      ${Input} {
        background: ${Colors.GRAY[5]}
        box-shadow: inset 0px 2px 6px rgba(0, 0, 0, 0.2);
      }
    `}

  ${({ inline }) =>
    inline &&
    css`
      width: 60%;
      min-width: 60%
  `}
  
  ${({ meta }) => (meta && meta.error ? errorInputMixin : "")}
    
  @media screen and (min-width: ${Breakpoints.md}) {
    margin-bottom: 0;
  }
`;

export const Option = styled.li`
  list-style: none;
  min-height: 42px;
  display: flex;
  align-items: center;
  padding: 10px;
  font-size: 14px;
  color: ${Colors.GRAY[0]};
  cursor: pointer;
  user-select: none;
  border-bottom: 1px solid ${Colors.GRAY[3]};
  
  ${({ isReadOnlyOption }) => isReadOnlyOption && css`
     opacity: 0.6;
     pointer-events: none;
  `}

  ${({ selected }) =>
    selected &&
    css`
      background-color: ${Colors.Alpha(Colors.SECONDARY, 0.7)};
    `}
    
   ${mediaHelper.sm`
      min-height: 42px;
   `}
`;

/* &:hover {
  background-color: ${Colors.GRAY[4]};
} */
export const OptionsContainer = styled.ul.attrs({ role: 'tablist' })`
  position: absolute;
  z-index: 1001;
  top:40px;
  max-height: 320px;
  left: 0;
  width: 100%;
  overflow-y: auto;
  overflow-x: hidden;
  margin: 0;
  padding: 0;
  box-sizing: border-box;
  border-left: 3px solid ${Colors.SECONDARY};
  border-right: 3px solid ${Colors.SECONDARY};
  border-bottom: 3px solid ${Colors.SECONDARY};
  background-color: #fff;
  border-bottom-left-radius: 8px;
  border-bottom-right-radius: 8px;
`;

export const Input = styled.input`
  width: ${({ smallInput }) => (smallInput ? "40px" : "100%")};
  min-width: 0;
  padding: 4px;
  border: 0;
  background: white;
  appearance: none;
  font-size: 14px;
  color: ${Colors.GRAY[0]};
  outline: none;
  box-sizing: border-box;
  text-align: ${({ alignCenter }) => (alignCenter ? "center" : "initial")};
  ${FiraSansBold}
  transition: background 0.15s ease, box-shadow 0.15s ease;
  scroll-margin: 40px;

  height: 42px;
  min-height: unset;
  
  @media screen and (min-width: ${Breakpoints.sm}) {
    height: 42px;
  }
  
  &::placeholder {
    color: ${({ placeholderColor }) => placeholderColor || Colors.GRAY[3]};
  }

  & .Select__indicator Select__dropdown-indicator {
    border-color: transparent transparent red;
  }
  
  &[disabled] {
    background-color: ${Colors.GRAY[4]};
    color: ${Colors.GRAY[0]};
  }
`;

export const IconButton = styled(Button).attrs({
  type: "button",
  tabIndex: -1,
})`
  padding: 0;
  & ${Icon} {
    font-size: 16px;
    color: ${Colors.GRAY[3]};
    margin: 0 12px;
  }
`;

const InputSpinner = styled(Spinner)`
  margin: 0 12px;
`;

const Wrapper = styled.div`
  position: relative;
  width: 100%;
  margin-bottom: ${({ filterField }) => (filterField ? '0' : '15px')};

  ${({ inline }) =>
    inline &&
    css`
    display: flex;
    justify-content: flex-start;
    flex-wrap: wrap;
  `}

  ${({ disabled, inverse }) =>
    disabled &&
    inverse &&
    css`
      opacity: 0.3;
    `}
`;


const ComboBox3 = ({
  warning,
  response,
  type,
  placeholder,
  disabled,
  readOnly,
  leftIcon,
  leftIconClick,
  rightIcon,
  rightIconClick,
  leftRender,
  rightRender,
  isLoading,
  label,
  field,
  format,
  parse,
  size,
  setLabelMinHeight,
  fieldProps,
  isRequired,
  tabIndex,
  classname,
  form: { setFieldValue, setFieldTouched, touched, errors, setFieldError },
  inputProps,
  validation,
  validationMinHeight,
  hideLabelAsterisk,
  smallInput,
  canGenerate,
  alignCenter,
  inline,
  inverseBlue,
  onInputRef,
  onChange,
  onBlur,
  onFocus,
  onPressEnter,
  onSearchSelect,
  options,
  extraLabelTop,
  filterField,
  additionalInfo,
  placeholderColor,
  refused,
  warningByHiddenQuestions,
  refusedByHiddenQuestions,
  onOpen,
  initialValue,
  ...props
}) => {

  const inputRef = useRef(null);
  const optionsRef = useRef(null);
  const selectionStart = useRef(null);
  const [isFocused, setIsFocused] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [usedInitialValue, setUsedInitialValue] = useState(false);

  const [optionList, setOptionList] = useState(false);

  const filteredList = option => {
    let options2 = placeholder ? [{ label: placeholder, value: "" }, ...options] : [{ label: "", value: "" }, ...options];
    
    if (option == '') {      
      setOptionList([...options2]);
      return;
    }
    
    let list = ([...options2]).filter(x => x.label?.trim()?.toUpperCase().includes(option?.trim()?.toUpperCase()));
    setOptionList(list);
  };


  useEffect(() => {
    if(initialValue && !usedInitialValue) {
      let opt = options.find(x => x.value == initialValue);
      if (opt) setLabelValue(opt.label);
      setUsedInitialValue(true);     
    } else if (field.value) {
      let opt = options.find(x => _.isEqual(x.value, field.value));
      if (opt) setLabelValue(opt.label);
    } else {
      if (!isOpen) {
        setLabelValue('');
      }
    }
  }, [field.value, isOpen, options]);

  useEffect(() => {
    if (
      inputRef &&
      inputRef.current &&
      inputRef.current.selectionStart &&
      (selectionStart.current || selectionStart.current === 0)
    ) {
      inputRef.current.selectionStart = selectionStart.current;
      inputRef.current.selectionEnd = selectionStart.current;
    }
  }, []);

  var touchedList;
  var touchedItem;
  var errorList;
  var errorItem;

  const metaFields = field?.name?.split('.');
  if (metaFields?.length == 3) {
    touchedList = touched[metaFields[0]]
    if (touchedList) {
      touchedItem = touchedList[parseInt(metaFields[1])]
    }
    errorList = errors[metaFields[0]]
    if (errorList) {
      errorItem = errorList[parseInt(metaFields[1])]
    }


  }

  const meta = {
    touched: metaFields?.length == 3 && touchedItem ? touchedItem[metaFields[2]] || touched[field?.name] : touched[field?.name],
    error: errors[field?.name] || '',
    isFocused,
    isOpen
  };

  const [labelValue, setLabelValue] = useState('');
  const _onFocus = () => {
    setIsFocused(true);
  };

  const _onBlur = () => {
    setIsFocused(false);
  };

  const [selectedResponse, setSelectedResponse] = useState(true);
  const [cursor, setCursor] = useState(0);
  const current_selection = useRef(null);
  const [onHover, setOnHover] = useState(false);
  const [timer, setTimer] = useState(false);
  useEffect(() => {

    if (current_selection.current && !onHover) {
      optionsRef.current.scrollTo(0, current_selection.current.offsetTop);
    }

  }, [cursor])

  useEffect(() => {
    if (isOpen) {
      onOpen && onOpen()
    }
  }, [isOpen])

  return (
    <Wrapper inline={inline} disabled={disabled} inverse={inverseBlue} filterField={filterField} {...props}>
      {extraLabelTop && (
        <Label
          meta={meta}
          weight={FontWeight.SemiBold}
          setMinHeight={setLabelMinHeight}
          hideLabelAsterisk={true}
          style={{ marginTop: 20, marginBottom: 20, marginLeft: inline ? "36%" : 0 }}
          bold
        >
          {extraLabelTop}
        </Label>
      )}

      {(label || setLabelMinHeight) && (
        <Label
          meta={meta}
          width={inline ? 35 : null}
          isRequired={isRequired}
          weight={FontWeight.SemiBold}
          setMinHeight={setLabelMinHeight}
          inline={inline}
          inverse={inverseBlue}
          hideLabelAsterisk={hideLabelAsterisk}
        >
          {label}
        </Label>
      )}

      {additionalInfo && (
        <Label>
          <sup>{additionalInfo}</sup>
        </Label>
      )}

      <InputContainer
        meta={meta}
        isFocused={meta.isFocused}
        isOpen={isOpen}
        inline={inline}
        inverseBlue={inverseBlue}
        warning={warning}
        refused={refused}
        warningByHiddenQuestions={warningByHiddenQuestions}
        refusedByHiddenQuestions={refusedByHiddenQuestions}
      >
        {leftIcon && (
          <IconButton onClick={leftIconClick}>
            <Icon name={leftIcon} />
          </IconButton>
        )}
        {leftRender && leftRender()}

        <Input
          autocomplete="off"
          autoComplete={"off"}
          ref={(ref) => {
            inputRef.current = ref;
            if (onInputRef) onInputRef(ref);
          }}
          size={size}
          type={type}
          placeholder={placeholder}
          disabled={disabled}
          readOnly={readOnly}
          tabIndex={tabIndex}
          {...field}
          placeholderColor={placeholderColor}
          onKeyDown={(e) => {
            // arrow up/down button should select next/previous list element
            if (e.key.toLowerCase() === "arrowup" && cursor > 0) {
              setCursor(cursor - 1);
              setOnHover(false);
            } else if (e.key.toLowerCase() === "arrowdown" && cursor < optionList.filter((x) => x.value != field.value).length - 1) {
              setCursor(cursor + 1);
              setOnHover(false);
            } else if (e.key.toLowerCase() === "arrowup") {
              setCursor(optionList.filter((x) => x.value != field.value).length - 1);
              setOnHover(false);
            } else if (e.key.toLowerCase() === "arrowdown") {
              setCursor(0);
              setOnHover(false);
            } else if (e.key.toLowerCase() === "enter") {
              e.preventDefault();
              e.stopPropagation();
              setIsOpen(false);
              inputRef.current.blur();
              onChange(optionList.filter((x) => x.value != field.value)[cursor].value);
              setSelectedResponse(true);
            }
          }}
          onFocus={() => {
            if (!readOnly) setIsOpen(true);
            setCursor(0);
            filteredList("");
            if (!selectedResponse) {
              setLabelValue("");
            }

            onFocus(field.value);
            _onFocus();
          }}
          onBlur={(e) => {
            e.stopPropagation();
            if (!selectedResponse) {
              setLabelValue("");
              setFieldValue(field.name, "");
              onChange("");
            }
            setIsOpen(false);
            onBlur(field.value, this);
            _onBlur();

            if (!meta.touched) {
              setFieldTouched(field.name, true);
            }
          }}
          smallInput={smallInput}
          inverseBlue={inverseBlue}
          alignCenter={alignCenter}
          value={labelValue}
          onChange={(e) => {
            if (meta.error) setFieldError(field.name, "");
            setLabelValue(e.target.value);
            filteredList(e.target.value);
            setFieldValue(field.name, "");
            setSelectedResponse(false);
          }}
          {...fieldProps}
        />
        <FontIcon
          onMouseDown={(e) => {
            if (disabled || readOnly) return;
            else {
              if (!isOpen && !timer) {
                setTimeout(() => {
                  setIsOpen(true);
                }, 100);
              } else {
                if (isOpen) {
                  setIsOpen(false);
                  setTimer(true);
                  setTimeout(() => {
                    setTimer(false);
                  }, 300);
                }
              }
            }
          }}
          name="arrow-down"
          isOpen={meta.isOpen}
        />

        {rightRender && rightRender()}
        {isLoading && <InputSpinner size={SpinnerSize.SMALL} />}

        {(isOpen && optionList?.length) > 0 && (
          <OptionsContainer
            inline={inline}
            ref={(ref) => {
              optionsRef.current = ref;
            }}
          >
            {optionList
              .filter((x) => x.value != (field.value || initialValue))
              .filter((x) => !x.expireDateAnswerOption)
              .map((x, i) => (
                <Option
                  key={`opt-${x.label}-${i}`}
                  isReadOnlyOption={x.isReadOnly}
                  onMouseOver={() => {
                    setCursor(i);
                    setOnHover(true);
                  }}
                  ref={(e) => (cursor === i ? (current_selection.current = e) : null)}
                  style={cursor === i ? { backgroundColor: Colors.GRAY[4] } : {}}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setIsOpen(false);
                    setFieldValue(field.name, x.value);
                    inputRef.current.blur();
                    onChange(x.value);
                    if (meta.error) setFieldError(field.name, "");
                    setSelectedResponse(true);
                  }}
                >
                  <Text weight={FontWeight.Regular}>{x.label}</Text>
                </Option>
              ))}
          </OptionsContainer>
        )}
      </InputContainer>

      {validation && (
        <ValidationError
          style={{ minWidth: "100%", position: "absolute", bottom: -38, paddingRight: inline ? 18 : 0 }}
          meta={meta}
          validationMinHeight={validationMinHeight}
        />
      )}
    </Wrapper>
  );
};

ComboBox3.propTypes = propTypes;
ComboBox3.defaultProps = defaultProps;

export default ComboBox3;
